import {
    ActivityData,
    ConversionStage,
    DashboardOverview,
    Forecast,
    getActivity,
    getConversionRates,
    getDashboardOverview,
    getForecast,
    getLeaderboard,
    getPipelineMetrics,
    getSentimentDistribution,
    getVelocity,
    LeaderboardEntry,
    PipelineMetrics,
    SentimentDistribution,
    Velocity,
} from '@/lib/api/sales/dashboard-api'
import { getPipelines, SalesPipeline } from '@/lib/api/sales/pipelines-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useState } from 'react'
import {
    MdArrowDownward,
    MdArrowUpward,
    MdAttachMoney,
    MdEmojiEvents,
    MdHandshake,
    MdLeaderboard,
    MdPeople,
    MdPercent,
    MdSchedule,
    MdSpeed
} from 'react-icons/md'
import { toast } from 'react-toastify'
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis, YAxis,
} from 'recharts'

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
    return (value / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatPercentage(value: number): string {
    return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
}

function formatDays(value: number): string {
    return `${Math.round(value)} ${Math.round(value) === 1 ? 'dia' : 'dias'}`
}

function getComparisonColor(value: number): string {
    if (value > 0) return 'text-success'
    if (value < 0) return 'text-error'
    return 'text-base-content/50'
}

function getComparisonIcon(value: number) {
    if (value > 0) return <MdArrowUpward className="inline" />
    if (value < 0) return <MdArrowDownward className="inline" />
    return null
}

function getDateRange(period: string): { startDate: string; endDate: string } {
    const end = new Date()
    const start = new Date()
    switch (period) {
        case '7d':
            start.setDate(end.getDate() - 7)
            break
        case '30d':
            start.setDate(end.getDate() - 30)
            break
        case '90d':
            start.setDate(end.getDate() - 90)
            break
        default:
            start.setDate(end.getDate() - 30)
    }
    return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
    }
}

const PERIOD_OPTIONS = [
    { value: '7d', label: '7 dias' },
    { value: '30d', label: '30 dias' },
    { value: '90d', label: '90 dias' },
]

const SENTIMENT_COLORS: Record<string, string> = {
    INTERESTED: '#60a5fa',
    VERY_SATISFIED: '#16a34a',
    SATISFIED: '#22c55e',
    NEUTRAL: '#3b82f6',
    CURIOUS: '#60a5fa',
    DISSATISFIED: '#f97316',
    FRUSTRATED: '#ea580c',
    ANGRY: '#ef4444',
    URGENT: '#8b5cf6',
}

const SENTIMENT_LABELS: Record<string, string> = {
    INTERESTED: 'Interessado',
    VERY_SATISFIED: 'Muito Satisfeito',
    SATISFIED: 'Satisfeito',
    NEUTRAL: 'Neutro',
    CURIOUS: 'Interessado',
    DISSATISFIED: 'Insatisfeito',
    FRUSTRATED: 'Frustrado',
    ANGRY: 'Irritado',
    URGENT: 'Urgente',
}

const tooltipStyle = {
    backgroundColor: 'oklch(var(--b1))',
    border: '1px solid oklch(var(--b3))',
    borderRadius: '8px',
}

// Paleta da marca Mediar (espelha o tema em index.css).
const CHART_COLORS = {
    primary: '#543C9A',
    secondary: '#15A0D4',
    accent: '#9DD011',
    info: '#3b82f6',
    warning: '#f59e0b',
}

// ── Skeleton Components ──────────────────────────────────────────────────────

function SkeletonCard() {
    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5 animate-pulse">
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-base-300" />
                <div className="flex-1 space-y-2">
                    <div className="h-3 w-24 bg-base-300 rounded" />
                    <div className="h-6 w-16 bg-base-300 rounded" />
                </div>
            </div>
        </div>
    )
}

function SkeletonChart({ title }: { title: string }) {
    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
            <h3 className="text-lg font-semibold mb-4">{title}</h3>
            <div className="h-[280px] bg-base-300/30 rounded-lg animate-pulse" />
        </div>
    )
}

function EmptyState({ message }: { message?: string }) {
    return (
        <div className="flex items-center justify-center h-64 text-base-content/50">
            {message || 'Nenhum dado encontrado para o período selecionado'}
        </div>
    )
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function SalesDashboardPage() {
    const auth = useAuth()
    const permissions = useResourcePermissions('sales', 'dashboard')

    // Filters
    const [period, setPeriod] = useState('30d')
    const [pipelineId, setPipelineId] = useState<number | undefined>(undefined)
    const [pipelines, setPipelines] = useState<SalesPipeline[]>([])

    // Data
    const [overview, setOverview] = useState<DashboardOverview | null>(null)
    const [pipelineMetrics, setPipelineMetrics] = useState<PipelineMetrics | null>(null)
    const [forecast, setForecast] = useState<Forecast | null>(null)
    const [velocity, setVelocity] = useState<Velocity | null>(null)
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
    const [conversion, setConversion] = useState<ConversionStage[]>([])
    const [sentiment, setSentiment] = useState<SentimentDistribution | null>(null)
    const [activity, setActivity] = useState<ActivityData | null>(null)

    // Loading
    const [loadingOverview, setLoadingOverview] = useState(true)
    const [loadingPipeline, setLoadingPipeline] = useState(true)
    const [loadingForecast, setLoadingForecast] = useState(true)
    const [loadingVelocity, setLoadingVelocity] = useState(true)
    const [loadingLeaderboard, setLoadingLeaderboard] = useState(true)
    const [loadingConversion, setLoadingConversion] = useState(true)
    const [loadingSentiment, setLoadingSentiment] = useState(true)
    const [loadingActivity, setLoadingActivity] = useState(true)

    const dateRange = useMemo(() => getDateRange(period), [period])

    // Load pipelines on mount
    useEffect(() => {
        getPipelines()
            .then((res) => {
                if (res.success && res.result) {
                    const result = res.result as any
                    const list = Array.isArray(result) ? result : (result.data ?? [])
                    setPipelines(list)
                    if (list.length > 0 && !pipelineId) {
                        setPipelineId(list[0].id)
                    }
                }
            })
            .catch(() => { })
    }, [])

    // Load overview + activity + leaderboard + sentiment (date-dependent)
    useEffect(() => {
        loadOverview()
        loadActivity()
        loadLeaderboard()
        loadSentiment()
    }, [period, pipelineId])

    // Load pipeline-specific data when pipelineId changes
    useEffect(() => {
        if (pipelineId) {
            loadPipelineMetrics()
            loadConversion()
            loadForecast()
            loadVelocity()
        }
    }, [pipelineId, period])

    async function loadOverview() {
        setLoadingOverview(true)
        try {
            const res = await getDashboardOverview({
                ...dateRange,
                pipelineId,
            })
            if (res.success && res.result) {
                setOverview(res.result)
            }
        } catch {
            toast.error('Erro ao carregar visão geral')
        } finally {
            setLoadingOverview(false)
        }
    }

    async function loadPipelineMetrics() {
        if (!pipelineId) return
        setLoadingPipeline(true)
        try {
            const res = await getPipelineMetrics(pipelineId, dateRange)
            if (res.success && res.result) {
                setPipelineMetrics(res.result)
            }
        } catch {
            toast.error('Erro ao carregar métricas do pipeline')
        } finally {
            setLoadingPipeline(false)
        }
    }

    async function loadForecast() {
        setLoadingForecast(true)
        try {
            const res = await getForecast({ pipelineId })
            if (res.success && res.result) {
                setForecast(res.result)
            }
        } catch {
            toast.error('Erro ao carregar previsão')
        } finally {
            setLoadingForecast(false)
        }
    }

    async function loadVelocity() {
        setLoadingVelocity(true)
        try {
            const res = await getVelocity({ pipelineId })
            if (res.success && res.result) {
                setVelocity(res.result)
            }
        } catch {
            toast.error('Erro ao carregar velocidade')
        } finally {
            setLoadingVelocity(false)
        }
    }

    async function loadLeaderboard() {
        setLoadingLeaderboard(true)
        try {
            const res = await getLeaderboard(dateRange)
            if (res.success && res.result) {
                setLeaderboard(res.result.rankings ?? [])
            }
        } catch {
            toast.error('Erro ao carregar ranking')
        } finally {
            setLoadingLeaderboard(false)
        }
    }

    async function loadConversion() {
        if (!pipelineId) return
        setLoadingConversion(true)
        try {
            const res = await getConversionRates(pipelineId)
            if (res.success && res.result) {
                setConversion(res.result.stages ?? [])
            }
        } catch {
            toast.error('Erro ao carregar conversão')
        } finally {
            setLoadingConversion(false)
        }
    }

    async function loadSentiment() {
        setLoadingSentiment(true)
        try {
            const res = await getSentimentDistribution(dateRange)
            if (res.success && res.result) {
                setSentiment(res.result)
            }
        } catch {
            toast.error('Erro ao carregar sentimentos')
        } finally {
            setLoadingSentiment(false)
        }
    }

    async function loadActivity() {
        setLoadingActivity(true)
        try {
            const res = await getActivity({
                ...dateRange,
                groupBy: period === '7d' ? 'day' : period === '30d' ? 'day' : 'week',
            })
            if (res.success && res.result) {
                setActivity(res.result)
            }
        } catch {
            toast.error('Erro ao carregar atividade')
        } finally {
            setLoadingActivity(false)
        }
    }

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <h1 className="text-2xl font-bold">Dashboard de Vendas</h1>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="tabs tabs-boxed">
                        {PERIOD_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                className={`tab ${period === opt.value ? 'tab-active' : ''}`}
                                onClick={() => setPeriod(opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    <select
                        className="select select-bordered select-sm"
                        value={pipelineId ?? ''}
                        onChange={(e) =>
                            setPipelineId(e.target.value ? Number(e.target.value) : undefined)
                        }
                    >
                        <option value="">Todos os pipelines</option>
                        {pipelines.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* ── KPI Cards ───────────────────────────────────────────────── */}
            {loadingOverview ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <SkeletonCard key={i} />
                    ))}
                </div>
            ) : overview ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {/* Total de Leads */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-secondary/10">
                                <MdPeople className="text-2xl text-secondary" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-base-content/60 truncate">Total de Leads</p>
                                <p className="text-xl font-bold">{overview.totalLeads.toLocaleString('pt-BR')}</p>
                                {overview.comparison && (
                                    <p className={`text-xs ${getComparisonColor(overview.comparison.leadsChange)}`}>
                                        {getComparisonIcon(overview.comparison.leadsChange)}
                                        {' '}{Math.abs(overview.comparison.leadsChange).toFixed(1)}%
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Deals Abertos */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-info/10">
                                <MdHandshake className="text-2xl text-info" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-base-content/60 truncate">Deals Abertos</p>
                                <p className="text-xl font-bold">{overview.openDeals.count}</p>
                                <p className="text-xs text-base-content/50">{formatCurrency(overview.openDeals.value)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Deals Ganhos */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-success/10">
                                <MdEmojiEvents className="text-2xl text-success" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-base-content/60 truncate">Deals Ganhos</p>
                                <p className="text-xl font-bold text-success">{overview.wonDeals.count}</p>
                                <p className="text-xs text-success/70">{formatCurrency(overview.wonDeals.value)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Taxa de Conversão */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-secondary/10">
                                <MdPercent className="text-2xl text-secondary" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-base-content/60 truncate">Taxa de Conversão</p>
                                <p className="text-xl font-bold">{formatPercentage(overview.conversionRate)}</p>
                                <div className="w-full bg-base-300 rounded-full h-1.5 mt-1">
                                    <div
                                        className="bg-secondary rounded-full h-1.5 transition-all"
                                        style={{ width: `${Math.min(overview.conversionRate, 100)}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Receita */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-accent/10">
                                <MdAttachMoney className="text-2xl text-accent" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-base-content/60 truncate">Receita</p>
                                <p className="text-xl font-bold">{formatCurrency(overview.wonDeals.value)}</p>
                                {overview.comparison && (
                                    <p className={`text-xs ${getComparisonColor(overview.comparison.revenueChange)}`}>
                                        {getComparisonIcon(overview.comparison.revenueChange)}
                                        {' '}{Math.abs(overview.comparison.revenueChange).toFixed(1)}%
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tempo Médio */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-warning/10">
                                <MdSchedule className="text-2xl text-warning" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-base-content/60 truncate">Tempo Médio</p>
                                <p className="text-xl font-bold">{formatDays(overview.avgTimeToClose)}</p>
                                <p className="text-xs text-base-content/50">
                                    Média: {formatCurrency(overview.avgDealValue)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center text-base-content/50 py-8">
                    Nenhum dado encontrado para o período selecionado
                </div>
            )}

            {/* ── Charts Row 1: Atividade + Pipeline Funnel ───────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Activity Chart */}
                {loadingActivity ? (
                    <SkeletonChart title="Atividade" />
                ) : (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <h3 className="text-lg font-semibold mb-4">Atividade</h3>
                        {!activity || activity.data.length === 0 ? (
                            <EmptyState />
                        ) : (
                            <div style={{ height: '300px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={activity.data}>
                                        <defs>
                                            <linearGradient id="leadsGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.3} />
                                                <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="dealsWonGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={CHART_COLORS.accent} stopOpacity={0.3} />
                                                <stop offset="100%" stopColor={CHART_COLORS.accent} stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis dataKey="period" fontSize={12} />
                                        <YAxis fontSize={12} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Legend />
                                        <Area
                                            type="monotone"
                                            dataKey="leadsCreated"
                                            stroke={CHART_COLORS.primary}
                                            strokeWidth={2}
                                            fill="url(#leadsGradient)"
                                            name="Leads Criados"
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="dealsWon"
                                            stroke={CHART_COLORS.accent}
                                            strokeWidth={2}
                                            fill="url(#dealsWonGradient)"
                                            name="Deals Ganhos"
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="dealsLost"
                                            stroke={CHART_COLORS.secondary}
                                            strokeWidth={1.5}
                                            fill="none"
                                            strokeDasharray="4 4"
                                            name="Deals Perdidos"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                )}

                {/* Pipeline Funnel */}
                {loadingPipeline ? (
                    <SkeletonChart title="Pipeline" />
                ) : (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <h3 className="text-lg font-semibold mb-4">
                            Pipeline{pipelineMetrics?.pipeline?.name ? `: ${pipelineMetrics.pipeline.name}` : ''}
                        </h3>
                        {!pipelineMetrics || pipelineMetrics.stages.length === 0 ? (
                            <EmptyState message="Selecione um pipeline para visualizar" />
                        ) : (
                            <div style={{ height: '300px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={pipelineMetrics.stages}
                                        layout="vertical"
                                        margin={{ left: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis type="number" fontSize={12} />
                                        <YAxis
                                            dataKey="name"
                                            type="category"
                                            fontSize={12}
                                            width={100}
                                        />
                                        <Tooltip
                                            contentStyle={tooltipStyle}
                                            formatter={((value: any, name: any) => {
                                                if (name === 'Valor Total') return [formatCurrency(Number(value)), name]
                                                return [value, name]
                                            }) as any}
                                        />
                                        <Legend />
                                        <Bar
                                            dataKey="dealCount"
                                            name="Deals"
                                            radius={[0, 4, 4, 0]}
                                        >
                                            {pipelineMetrics.stages.map((stage) => (
                                                <Cell
                                                    key={stage.id}
                                                    fill={stage.color || CHART_COLORS.primary}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Forecast Section ────────────────────────────────────────── */}
            {loadingForecast ? (
                <SkeletonChart title="Previsão de Receita" />
            ) : forecast ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                    <h3 className="text-lg font-semibold mb-4">Previsão de Receita</h3>

                    {/* Forecast KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-base-200/50 rounded-lg p-4 text-center">
                            <p className="text-xs text-base-content/60 mb-1">Melhor Cenário</p>
                            <p className="text-lg font-bold text-secondary">{formatCurrency(forecast.bestCase)}</p>
                        </div>
                        <div className="bg-base-200/50 rounded-lg p-4 text-center">
                            <p className="text-xs text-base-content/60 mb-1">Mais Provável</p>
                            <p className="text-lg font-bold text-info">{formatCurrency(forecast.mostLikely)}</p>
                        </div>
                        <div className="bg-base-200/50 rounded-lg p-4 text-center">
                            <p className="text-xs text-base-content/60 mb-1">Comprometido</p>
                            <p className="text-lg font-bold text-warning">{formatCurrency(forecast.committed)}</p>
                        </div>
                        <div className="bg-base-200/50 rounded-lg p-4 text-center">
                            <p className="text-xs text-base-content/60 mb-1">Já Fechado</p>
                            <p className="text-lg font-bold text-success">{formatCurrency(forecast.closed)}</p>
                        </div>
                    </div>

                    {/* Forecast Chart */}
                    {forecast.byMonth.length === 0 ? (
                        <EmptyState message="Sem dados de previsão mensal" />
                    ) : (
                        <div style={{ height: '280px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={forecast.byMonth}>
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                    <XAxis dataKey="month" fontSize={12} />
                                    <YAxis
                                        fontSize={12}
                                        tickFormatter={(v) => formatCurrency(v)}
                                    />
                                    <Tooltip
                                        contentStyle={tooltipStyle}
                                        formatter={((value: any, name: any) => [formatCurrency(Number(value)), name || '']) as any}
                                    />
                                    <Legend />
                                    <Bar
                                        dataKey="projected"
                                        name="Projetado"
                                        fill={CHART_COLORS.info}
                                        radius={[4, 4, 0, 0]}
                                        opacity={0.7}
                                    />
                                    <Bar
                                        dataKey="closed"
                                        name="Fechado"
                                        fill={CHART_COLORS.accent}
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            ) : null}

            {/* ── Charts Row 2: Sentimento + Conversão ────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Sentiment Distribution */}
                {loadingSentiment ? (
                    <SkeletonChart title="Distribuição de Sentimentos" />
                ) : (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <h3 className="text-lg font-semibold mb-4">Distribuição de Sentimentos</h3>
                        {!sentiment || sentiment.distribution.length === 0 ? (
                            <EmptyState />
                        ) : (
                            <>
                                <div style={{ height: '240px' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={sentiment.distribution}
                                                dataKey="count"
                                                nameKey="sentiment"
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={55}
                                                outerRadius={90}
                                                paddingAngle={2}
                                            >
                                                {sentiment.distribution.map((entry) => (
                                                    <Cell
                                                        key={entry.sentiment}
                                                        fill={SENTIMENT_COLORS[entry.sentiment] ?? '#6b7280'}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={tooltipStyle}
                                                formatter={(value: any, name: any) => [
                                                    value,
                                                    SENTIMENT_LABELS[name as string] ?? name,
                                                ]}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="flex flex-wrap justify-center gap-3 mt-2">
                                    {sentiment.distribution.map((entry) => (
                                        <div key={entry.sentiment} className="flex items-center gap-1.5 text-xs">
                                            <span
                                                className="w-2.5 h-2.5 rounded-full inline-block"
                                                style={{ backgroundColor: SENTIMENT_COLORS[entry.sentiment] ?? '#6b7280' }}
                                            />
                                            <span>{SENTIMENT_LABELS[entry.sentiment] ?? entry.sentiment}</span>
                                            <span className="text-base-content/50">({formatPercentage(entry.percentage)})</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Conversion Funnel */}
                {loadingConversion ? (
                    <SkeletonChart title="Funil de Conversão" />
                ) : (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <h3 className="text-lg font-semibold mb-4">Funil de Conversão</h3>
                        {conversion.length === 0 ? (
                            <EmptyState message="Selecione um pipeline para visualizar a conversão" />
                        ) : (
                            <div style={{ height: '300px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={conversion}
                                        layout="vertical"
                                        margin={{ left: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis
                                            type="number"
                                            fontSize={12}
                                            domain={[0, 100]}
                                            tickFormatter={(v) => `${v}%`}
                                        />
                                        <YAxis
                                            dataKey="name"
                                            type="category"
                                            fontSize={12}
                                            width={100}
                                        />
                                        <Tooltip
                                            contentStyle={tooltipStyle}
                                            formatter={(value: any) => [
                                                `${Number(value).toFixed(1)}%`,
                                                'Taxa de Conversão',
                                            ]}
                                        />
                                        <Bar
                                            dataKey="conversionRate"
                                            name="Conversão"
                                            radius={[0, 4, 4, 0]}
                                        >
                                            {conversion.map((stage) => (
                                                <Cell
                                                    key={stage.id}
                                                    fill={stage.color || CHART_COLORS.primary}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Tables Row: Leaderboard + Velocity ──────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Leaderboard */}
                {loadingLeaderboard ? (
                    <SkeletonChart title="Ranking de Vendedores" />
                ) : (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <MdLeaderboard className="text-xl text-secondary" />
                            <h3 className="text-lg font-semibold">Ranking de Vendedores</h3>
                        </div>
                        {leaderboard.length === 0 ? (
                            <EmptyState message="Nenhum vendedor encontrado no período" />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Vendedor</th>
                                            <th className="text-right">Ganhos</th>
                                            <th className="text-right">Valor Total</th>
                                            <th className="text-right">Conversão</th>
                                            <th className="text-right">Tempo Médio</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leaderboard.map((entry, idx) => (
                                            <tr key={entry.userId} className="hover">
                                                <td>
                                                    <span className={`badge badge-sm ${idx === 0 ? 'badge-warning' :
                                                        idx === 1 ? 'badge-ghost' :
                                                            idx === 2 ? 'badge-accent' : 'badge-ghost'
                                                        }`}>
                                                        {idx + 1}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="flex items-center gap-2">
                                                        <div className="avatar placeholder">
                                                            <div className="bg-secondary/10 text-secondary rounded-full w-8">
                                                                <span className="text-xs">
                                                                    {entry.userName
                                                                        .split(' ')
                                                                        .slice(0, 2)
                                                                        .map((n) => n[0])
                                                                        .join('')
                                                                        .toUpperCase()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <span className="font-medium text-sm">{entry.userName}</span>
                                                    </div>
                                                </td>
                                                <td className="text-right font-semibold">{entry.wonDeals}</td>
                                                <td className="text-right">{formatCurrency(entry.totalValue)}</td>
                                                <td className="text-right">{formatPercentage(entry.conversionRate)}</td>
                                                <td className="text-right text-base-content/70">{formatDays(entry.avgDaysToClose)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Pipeline Velocity */}
                {loadingVelocity ? (
                    <SkeletonChart title="Velocidade do Pipeline" />
                ) : (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <MdSpeed className="text-xl text-secondary" />
                            <h3 className="text-lg font-semibold">Velocidade do Pipeline</h3>
                        </div>
                        {!velocity || velocity.stages.length === 0 ? (
                            <EmptyState message="Selecione um pipeline para visualizar a velocidade" />
                        ) : (
                            <>
                                {/* Summary */}
                                <div className="flex flex-wrap gap-4 mb-4">
                                    <div className="bg-base-200/50 rounded-lg px-4 py-2">
                                        <p className="text-xs text-base-content/60">Média Total</p>
                                        <p className="text-lg font-bold">{formatDays(velocity.avgTotalDays)}</p>
                                    </div>
                                    {velocity.fastest && (
                                        <div className="bg-success/5 rounded-lg px-4 py-2">
                                            <p className="text-xs text-success/70">Mais Rápido</p>
                                            <p className="text-sm font-semibold text-success">
                                                {velocity.fastest.title} ({formatDays(velocity.fastest.days)})
                                            </p>
                                        </div>
                                    )}
                                    {velocity.slowest && (
                                        <div className="bg-error/5 rounded-lg px-4 py-2">
                                            <p className="text-xs text-error/70">Mais Lento</p>
                                            <p className="text-sm font-semibold text-error">
                                                {velocity.slowest.title} ({formatDays(velocity.slowest.days)})
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Stages Table */}
                                <div className="overflow-x-auto">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Etapa</th>
                                                <th className="text-right">Tempo Médio</th>
                                                <th className="text-right">Deals</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {velocity.stages.map((stage) => (
                                                <tr key={stage.id} className="hover">
                                                    <td>
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="w-3 h-3 rounded-full inline-block flex-shrink-0"
                                                                style={{ backgroundColor: stage.color || CHART_COLORS.primary }}
                                                            />
                                                            <span className="font-medium">{stage.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="text-right">{formatDays(stage.avgDays)}</td>
                                                    <td className="text-right">{stage.dealCount}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
